import React from "react";
import WebProfitSpinner from "../system/WebProfitSpinner";
import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import DomainRoundedIcon from "@mui/icons-material/DomainRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import LanguageRoundedIcon from "@mui/icons-material/LanguageRounded";
import MoreHorizRoundedIcon from "@mui/icons-material/MoreHorizRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import PublishRoundedIcon from "@mui/icons-material/PublishRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import ReplayRoundedIcon from "@mui/icons-material/ReplayRounded";
import SettingsRoundedIcon from "@mui/icons-material/SettingsRounded";
import { useNavigate } from "react-router-dom";
import { projectsApi } from "../../api/projects";
import { settingsApi } from "../../api/settings";
import { useProjects } from "../../context/ProjectsContext";
import {
  canRetryProjectDeployment,
  getProjectManagedUrl,
  getProjectPublicUrl,
  isProjectDeploymentPending,
} from "../../utils/projectDeployment";
import ProjectCustomDomainCard from "./ProjectCustomDomainCard";
import SharedStorefrontMigrationPanel from "./SharedStorefrontMigrationPanel";

const BRAND = { orange: "#F35A3A", dark: "var(--wp-panel-text-primary, #19242F)" };

function projectKey(project) {
  return project?.id || project?._id || project?.slug || "";
}

function clean(value) {
  return String(value || "").trim();
}

function domainStates(project) {
  return Array.isArray(project?.deployment?.customDomains)
    ? project.deployment.customDomains.filter((entry) => clean(entry?.name))
    : [];
}

function activeDomain(project) {
  const activeNames = new Set(
    (Array.isArray(project?.domains) ? project.domains : [])
      .map((value) => clean(value).toLowerCase())
      .filter(Boolean),
  );
  return domainStates(project).find((entry) => {
    const name = clean(entry?.name).toLowerCase();
    return name && (entry?.active === true || activeNames.has(name)) && clean(entry?.status).toLowerCase() === "live";
  }) || null;
}

function currentDomainState(project) {
  return activeDomain(project) || domainStates(project)[0] || null;
}

function domainPresentation(project, entitlement) {
  const state = currentDomainState(project);
  const name = clean(state?.name);
  const status = clean(state?.status).toLowerCase();
  const dnsStatus = clean(state?.dnsStatus).toLowerCase();
  const hasError = ["failed", "https_error", "dns_error"].includes(status) || dnsStatus === "error" || Boolean(clean(state?.errorMessage));
  const active = Boolean(activeDomain(project));

  if (!state) {
    if (entitlement?.reason === "unavailable") {
      return { key: "unavailable", label: "No disponible", detail: "No se pudo comprobar el plan", severity: "default", action: "none", needsAttention: false };
    }
    return entitlement?.included === false
      ? { key: "locked", label: "Requiere módulo pago", detail: "La Landing gratis usa la dirección de WebProfit", severity: "default", action: "plan", needsAttention: false }
      : { key: "empty", label: "No conectado", detail: "Sin dominio personalizado", severity: "default", action: "connect", needsAttention: false };
  }

  if (active) {
    return { key: "active", label: "Conectado", detail: name, severity: "success", action: "manage", needsAttention: false };
  }
  if (hasError) {
    return { key: "error", label: "Requiere atención", detail: name, severity: "error", action: "resolve", needsAttention: true };
  }
  if (["pending_dns", "not_registered"].includes(status) || (status === "registering" && !state?.renderRegistered)) {
    return { key: "configure", label: "Requiere configuración", detail: name, severity: "warning", action: "configure", needsAttention: true };
  }
  if (["pending_https", "registering"].includes(status)) {
    return { key: "waiting", label: "Verificando", detail: name, severity: "info", action: "manage", needsAttention: false };
  }
  return { key: "configure", label: "Requiere configuración", detail: name, severity: "warning", action: "configure", needsAttention: true };
}

function publicationPresentation(settings, project) {
  const publicStatus = clean(settings?.publicStatus || "draft").toLowerCase();
  const draftRevision = Math.max(0, Number(settings?.draftRevision || settings?.revision || 0));
  const publishedRevision = Math.max(0, Number(settings?.publishedRevision || 0));
  const hasPublishedRevision = publishedRevision > 0;
  const hasUnpublishedChanges = hasPublishedRevision && draftRevision > publishedRevision;
  const deploymentStatus = clean(project?.deployment?.status || "not_requested").toLowerCase();

  let label = "No publicada";
  let severity = "default";
  if (publicStatus === "maintenance" && hasPublishedRevision) {
    label = "En mantenimiento";
    severity = "warning";
  } else if (publicStatus === "published" && hasPublishedRevision) {
    label = "Publicada";
    severity = "success";
  }

  return {
    publicStatus,
    draftRevision,
    publishedRevision,
    hasPublishedRevision,
    hasUnpublishedChanges,
    label,
    severity,
    deploymentStatus,
    deploymentFailed: deploymentStatus === "failed",
    deploymentPending: deploymentStatus === "provisioning",
  };
}

async function copyText(value) {
  const text = clean(value);
  if (!text) return false;
  if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return true;
  }
  const input = document.createElement("textarea");
  input.value = text;
  input.style.position = "fixed";
  input.style.opacity = "0";
  document.body.appendChild(input);
  input.select();
  const copied = document.execCommand("copy");
  input.remove();
  return copied;
}

function StatusRow({ icon, label, status, severity = "default", detail, action }) {
  return (
    <Stack direction="row" spacing={1.1} alignItems="center" sx={{ minWidth: 0, py: 1.05 }}>
      <Box sx={{ width: 34, height: 34, borderRadius: 2, display: "grid", placeItems: "center", bgcolor: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.05)", color: BRAND.dark, flexShrink: 0 }}>
        {icon}
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="caption" sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)", fontWeight: 900 }}>{label}</Typography>
        <Stack direction="row" spacing={0.7} alignItems="center" useFlexGap flexWrap="wrap" sx={{ mt: 0.15 }}>
          {status ? <Chip size="small" label={status} color={severity} variant={severity === "success" ? "filled" : "outlined"} sx={{ fontWeight: 900, height: 24 }} /> : null}
          {detail ? <Typography variant="body2" sx={{ color: BRAND.dark, fontWeight: 800, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: { xs: "62vw", md: 420 } }}>{detail}</Typography> : null}
        </Stack>
      </Box>
      {action ? <Box sx={{ flexShrink: 0 }}>{action}</Box> : null}
    </Stack>
  );
}

export default function ProjectPublicationDomainPanel({
  project,
  canManage = false,
  canRetry = false,
  canManageInfrastructure = false,
  onProjectChange,
}) {
  const navigate = useNavigate();
  const { refreshProjectDeployment, retryProjectDeployment } = useProjects();
  const [settings, setSettings] = React.useState(null);
  const [entitlement, setEntitlement] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [busy, setBusy] = React.useState("");
  const [error, setError] = React.useState("");
  const [notice, setNotice] = React.useState("");
  const [domainOpen, setDomainOpen] = React.useState(false);
  const [technicalOpen, setTechnicalOpen] = React.useState(false);
  const [menuAnchor, setMenuAnchor] = React.useState(null);

  const key = projectKey(project);
  const publicUrl = getProjectPublicUrl(project);
  const managedUrl = getProjectManagedUrl(project);
  const publication = publicationPresentation(settings, project);
  const domain = domainPresentation(project, entitlement);
  const retryAllowed = canRetry && canRetryProjectDeployment(project);

  const loadSummary = React.useCallback(async () => {
    if (!key) return;
    try {
      setLoading(true);
      setError("");
      const [settingsResult, entitlementResult] = await Promise.allSettled([
        settingsApi.get(key),
        projectsApi.getDomainEntitlement(key),
      ]);
      if (settingsResult.status === "fulfilled") {
        setSettings(settingsResult.value?.settings || null);
      } else {
        setError(settingsResult.reason?.message || "No se pudo consultar el estado editorial de la web.");
      }
      if (entitlementResult.status === "fulfilled") {
        setEntitlement(entitlementResult.value?.entitlement || null);
      } else {
        setEntitlement({ included: false, reason: "unavailable" });
      }
    } finally {
      setLoading(false);
    }
  }, [key]);

  React.useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  React.useEffect(() => {
    if (!key || !isProjectDeploymentPending(project) || publication.deploymentStatus !== "provisioning") return undefined;
    const timer = window.setInterval(() => {
      refreshProjectDeployment(key)
        .then((nextProject) => nextProject && onProjectChange?.(nextProject))
        .catch(() => null);
    }, 10000);
    return () => window.clearInterval(timer);
  }, [key, onProjectChange, project, publication.deploymentStatus, refreshProjectDeployment]);

  const applyProject = React.useCallback((nextProject) => {
    if (nextProject) onProjectChange?.(nextProject);
    return nextProject;
  }, [onProjectChange]);

  const refreshTechnical = async () => {
    if (!key || busy) return;
    try {
      setBusy("refresh");
      setError("");
      applyProject(await refreshProjectDeployment(key));
      setNotice("Estado actualizado.");
    } catch (requestError) {
      setError(requestError?.message || "No se pudo actualizar el estado técnico.");
    } finally {
      setBusy("");
    }
  };

  const retryTechnical = async () => {
    if (!key || busy || !retryAllowed) return;
    try {
      setBusy("retry");
      setError("");
      applyProject(await retryProjectDeployment(key));
      setNotice("Reintento iniciado.");
    } catch (requestError) {
      setError(requestError?.message || "No se pudo reintentar el despliegue.");
    } finally {
      setBusy("");
    }
  };

  const copyUrl = async () => {
    try {
      if (await copyText(publicUrl)) setNotice("URL copiada.");
    } catch {
      setError("No se pudo copiar la URL.");
    }
  };

  const openPublic = () => {
    if (publicUrl) window.open(publicUrl, "_blank", "noopener,noreferrer");
  };

  const openEditorPublish = () => navigate(`/editor-web/${encodeURIComponent(key)}?action=publish`);
  const openPlan = () => navigate(`/dashboard/proyectos/${encodeURIComponent(key)}/configuracion/suscripcion`);

  let primary = null;
  if (canManage && domain.needsAttention && entitlement?.included !== false) {
    primary = { label: domain.key === "error" ? "Resolver dominio" : "Configurar dominio", action: () => setDomainOpen(true), icon: <DomainRoundedIcon /> };
  } else if (canManage && (!publication.hasPublishedRevision || publication.publicStatus === "draft")) {
    primary = { label: "Publicar", action: openEditorPublish, icon: <PublishRoundedIcon /> };
  } else if (publication.deploymentFailed && retryAllowed) {
    primary = { label: "Reintentar", action: retryTechnical, icon: <ReplayRoundedIcon /> };
  } else if (publicUrl && publication.hasPublishedRevision) {
    primary = { label: "Ver web", action: openPublic, icon: <OpenInNewRoundedIcon /> };
  }

  const domainAction = entitlement?.reason === "unavailable"
    ? null
    : entitlement?.included === false
      ? <Button size="small" onClick={openPlan} sx={{ textTransform: "none", fontWeight: 900 }}>Ver plan</Button>
      : canManage
      ? <Button size="small" onClick={() => setDomainOpen((value) => !value)} sx={{ textTransform: "none", fontWeight: 900 }}>{domain.action === "connect" ? "Conectar" : domain.action === "resolve" ? "Resolver" : domain.action === "configure" ? "Configurar" : "Gestionar"}</Button>
      : null;

  return (
    <Stack spacing={1.2} sx={{ minWidth: 0 }}>
      <Paper variant="outlined" sx={{ borderRadius: 3, px: { xs: 1.25, sm: 1.5 }, py: 0.35, borderColor: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.10)" }}>
        {loading ? (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 2 }}>
            <WebProfitSpinner size={18} />
            <Typography variant="body2" color="text.secondary">Consultando publicación y dominio…</Typography>
          </Stack>
        ) : settings ? (
          <>
            <StatusRow
              icon={publication.publicStatus === "published" ? <CheckCircleRoundedIcon fontSize="small" /> : publication.deploymentFailed ? <ErrorOutlineRoundedIcon fontSize="small" /> : <LanguageRoundedIcon fontSize="small" />}
              label="Web"
              status={publication.label}
              severity={publication.severity}
              detail={publication.hasUnpublishedChanges ? "Hay cambios sin publicar" : publication.deploymentPending ? "Actualizando web…" : publication.deploymentFailed ? "Despliegue requiere atención" : ""}
            />
            <Box sx={{ borderTop: "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),.07)" }} />
            <StatusRow
              icon={<LanguageRoundedIcon fontSize="small" />}
              label="Dirección"
              detail={publicUrl || "Disponible después de publicar"}
              action={publicUrl ? (
                <Tooltip title="Copiar URL">
                  <IconButton size="small" onClick={copyUrl} aria-label="Copiar URL pública"><ContentCopyRoundedIcon fontSize="small" /></IconButton>
                </Tooltip>
              ) : null}
            />
            <Box sx={{ borderTop: "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),.07)" }} />
            <StatusRow
              icon={<DomainRoundedIcon fontSize="small" />}
              label="Dominio personalizado"
              status={domain.label}
              severity={domain.severity}
              detail={domain.detail}
              action={domainAction}
            />
          </>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>No se pudo resolver el estado editorial de la web.</Typography>
        )}
      </Paper>

      {error ? <Alert severity="error" action={<Button color="inherit" size="small" onClick={loadSummary}>Reintentar</Button>}>{error}</Alert> : null}
      {notice ? <Alert severity="success" onClose={() => setNotice("")}>{notice}</Alert> : null}

      <Stack direction={{ xs: "column", sm: "row" }} spacing={0.8} alignItems={{ sm: "center" }}>
        {primary ? (
          <Button
            type="button"
            variant="contained"
            startIcon={busy === "retry" ? <WebProfitSpinner size={16} color="inherit" /> : primary.icon}
            disabled={Boolean(busy)}
            onClick={primary.action}
            sx={{ minHeight: 42, borderRadius: 999, bgcolor: BRAND.orange, textTransform: "none", fontWeight: 950, px: 2.2 }}
          >
            {primary.label}
          </Button>
        ) : null}

        <Button
          type="button"
          variant="text"
          endIcon={<SettingsRoundedIcon />}
          onClick={() => setTechnicalOpen((value) => !value)}
          sx={{ textTransform: "none", fontWeight: 850, color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)" }}
        >
          Más opciones
        </Button>
        <IconButton size="small" aria-label="Más acciones" onClick={(event) => setMenuAnchor(event.currentTarget)} sx={{ ml: { sm: "auto !important" } }}>
          <MoreHorizRoundedIcon />
        </IconButton>
      </Stack>

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
        {publicUrl ? <MenuItem onClick={() => { setMenuAnchor(null); openPublic(); }}>Abrir web</MenuItem> : null}
        {publicUrl ? <MenuItem onClick={() => { setMenuAnchor(null); copyUrl(); }}>Copiar URL</MenuItem> : null}
        {canManage && publication.hasUnpublishedChanges ? <MenuItem onClick={() => { setMenuAnchor(null); openEditorPublish(); }}><PublishRoundedIcon fontSize="small" sx={{ mr: 1 }} />Publicar cambios</MenuItem> : null}
        {canManage && publication.publicStatus === "maintenance" ? <MenuItem onClick={() => { setMenuAnchor(null); navigate(`/editor-web/${encodeURIComponent(key)}`); }}><SettingsRoundedIcon fontSize="small" sx={{ mr: 1 }} />Gestionar publicación</MenuItem> : null}
        <MenuItem disabled={Boolean(busy)} onClick={() => { setMenuAnchor(null); refreshTechnical(); }}><RefreshRoundedIcon fontSize="small" sx={{ mr: 1 }} />Actualizar estado</MenuItem>
        {retryAllowed ? <MenuItem disabled={Boolean(busy) || publication.deploymentPending} onClick={() => { setMenuAnchor(null); retryTechnical(); }}><ReplayRoundedIcon fontSize="small" sx={{ mr: 1 }} />Reintentar despliegue</MenuItem> : null}
      </Menu>

      <Collapse in={domainOpen} timeout="auto" unmountOnExit>
        <ProjectCustomDomainCard
          project={project}
          compact
          canManage={canManage && entitlement?.included !== false}
          readOnlyMessage={entitlement?.included === false ? "El dominio existente se conserva. Activá cualquier módulo pago para volver a usarlo después del trial." : undefined}
          onProjectChange={(nextProject) => {
            applyProject(nextProject);
            loadSummary();
          }}
        />
      </Collapse>

      <Collapse in={technicalOpen} timeout="auto" unmountOnExit>
        <Stack spacing={1.1} sx={{ pt: 0.3 }}>
          <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2.5, bgcolor: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.015)" }}>
            <Stack spacing={0.45}>
              <Typography variant="caption" sx={{ fontWeight: 950, color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)" }}>DETALLE TÉCNICO</Typography>
              <Typography variant="body2" sx={{ color: BRAND.dark }}>
                Estado de despliegue: {publication.deploymentPending ? "Actualizando" : publication.deploymentFailed ? "Fallido" : clean(project?.deployment?.status || "Pendiente")}
              </Typography>
              {managedUrl && managedUrl !== publicUrl ? (
                <Stack direction="row" spacing={0.7} alignItems="center" sx={{ minWidth: 0 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>Dirección WebProfit: {managedUrl}</Typography>
                  <IconButton size="small" onClick={() => copyText(managedUrl)} aria-label="Copiar dirección WebProfit"><ContentCopyRoundedIcon fontSize="inherit" /></IconButton>
                </Stack>
              ) : null}
            </Stack>
          </Paper>
          {canManageInfrastructure ? (
            <SharedStorefrontMigrationPanel project={project} compact canManage onProjectChange={applyProject} />
          ) : null}
        </Stack>
      </Collapse>
    </Stack>
  );
}
